/**
 * The short question "Ask Iris" puts in the user's bubble (CU-869fkdhjv phase 3, UX § d and Obs. 6).
 *
 * One form for every element: verb + element, a colon, then the element's labels joined by " · " — labels of
 * the screen, never ids (criterion 28) and no evidence figure, except the element's own when it is what is
 * asked (a KPI, a count, a gap), which the caller passes explicitly as `{ value }`.
 *
 * A label is one of:
 *   - a string — shown as is, unless it is a bare number (that is an id where a label was expected: dropped);
 *   - `{ date }` — a pick-up date, short ("11 Nov" / "11 nov");
 *   - `{ range: [from, to] }` — "10–23 Nov";
 *   - `{ days }` — a rental length ("7 days" / "7 días" / "7 Tage" …);
 *   - `{ lastDays }` — a period ("last 7 days" / "últimos 7 días");
 *   - `{ events }` — a count of events ("248 events" / "248 eventos");
 *   - `{ value }` — the element's own figure, verbatim;
 *   - `{ week }` — a capture week ("Week 46" / "semana 46"), Price curve's Week by week;
 *   - `{ offices }` — a count of offices ("5 offices" / "5 oficinas"), My office network's groups;
 *   - `{ since }` — the start of a comparison, a date ("since 21 Oct" / "desde 21 oct");
 *   - `{ compare }` — Season's "Compare with": `ly` / `lw` / `yday` ("vs last year" / "vs año pasado");
 *   - `{ rank: [n, of] }` — a position ("5th of 9" / "5.º de 9").
 *
 * BI kinds (CU-869fkdhjv phase 3 wave 2): `change`, `curve`, `distance`, `marketMove`, `group`; the cards and the
 * rankings reuse `count`, the Week-by-week cell reuses `cell`.
 *
 * Languages: en, es, pt, fr, de (pt/fr/de added with Iris's language setting, Curro 2026-10-28); any other
 * code asks in English.
 *
 * Pure and alias-free (TL R4).
 */
// French puts a no-break space before ":" and "?"
const FR = text => text.replace(/ ?([:?])$/, '\u00a0$1')

const PREFIX = {
	cell:            { en: 'Explain this price:', es: 'Explícame este precio:', pt: 'Explica-me este preço:', fr: FR('Explique-moi ce prix:'), de: 'Erkläre mir diesen Preis:' },
	cellDecisions:   { en: 'Explain this price and the repricing decision:', es: 'Explícame este precio y la decisión de repricing:', pt: 'Explica-me este preço e a decisão de repricing:', fr: FR('Explique-moi ce prix et la décision de repricing:'), de: 'Erkläre mir diesen Preis und die Repricing-Entscheidung:' },
	rankCell:        { en: 'Explain this gap:', es: 'Explícame esta diferencia:', pt: 'Explica-me esta diferença:', fr: FR('Explique-moi cet écart:'), de: 'Erkläre mir diesen Abstand:' },
	chart:           { en: 'Explain this chart:', es: 'Explícame este gráfico:', pt: 'Explica-me este gráfico:', fr: FR('Explique-moi ce graphique:'), de: 'Erkläre mir dieses Diagramm:' },
	evolution:       { en: 'How has this price evolved?', es: '¿Cómo ha evolucionado este precio?', pt: 'Como evoluiu este preço?', fr: FR('Comment ce prix a-t-il évolué?'), de: 'Wie hat sich dieser Preis entwickelt?' },
	reading:         { en: 'Explain this reading:', es: 'Explícame esta lectura:', pt: 'Explica-me esta leitura:', fr: FR('Explique-moi cette lecture:'), de: 'Erkläre mir diese Auswertung:' },
	kpi:             { en: s => `Explain my ${s}:`, es: s => `Explícame mi ${s}:`, pt: s => `Explica-me o meu ${s}:`, fr: s => FR(`Explique-moi mon ${s}:`), de: s => `Erkläre mir meine Kennzahl ${s}:` },
	count:           { en: s => `Explain ${s}:`, es: s => `Explícame ${s}:`, pt: s => `Explica-me ${s}:`, fr: s => FR(`Explique-moi ${s}:`), de: s => `Erkläre mir ${s}:` },
	countTotal:      { en: 'Explain this period’s activity:', es: 'Explícame la actividad del periodo:', pt: 'Explica-me a atividade do período:', fr: FR('Explique-moi l’activité de la période:'), de: 'Erkläre mir die Aktivität des Zeitraums:' },
	'event.capped':  { en: 'Why was this price capped?', es: '¿Por qué se recortó este precio?', pt: 'Porque é que este preço foi limitado?', fr: FR('Pourquoi ce prix a-t-il été plafonné?'), de: 'Warum wurde dieser Preis gedeckelt?' },
	'event.held':    { en: 'Why is this decision held?', es: '¿Por qué está retenida esta decisión?', pt: 'Porque é que esta decisão está retida?', fr: FR('Pourquoi cette décision est-elle retenue?'), de: 'Warum wird diese Entscheidung zurückgehalten?' },
	'event.failed':  { en: 'Why did this send fail?', es: '¿Por qué falló este envío?', pt: 'Porque é que este envio falhou?', fr: FR('Pourquoi cet envoi a-t-il échoué?'), de: 'Warum ist diese Übermittlung fehlgeschlagen?' },
	'event.applied': { en: 'Why did the engine set this price?', es: '¿Por qué el motor puso este precio?', pt: 'Porque é que o motor definiu este preço?', fr: FR('Pourquoi le moteur a-t-il fixé ce prix?'), de: 'Warum hat die Engine diesen Preis gesetzt?' },
	'event.generic': { en: 'Why did the engine set this price?', es: '¿Por qué el motor puso este precio?', pt: 'Porque é que o motor definiu este preço?', fr: FR('Pourquoi le moteur a-t-il fixé ce prix?'), de: 'Warum hat die Engine diesen Preis gesetzt?' },
	change:          { en: 'Explain this change:', es: 'Explícame este cambio:', pt: 'Explica-me esta variação:', fr: FR('Explique-moi cette variation:'), de: 'Erkläre mir diese Veränderung:' },
	curve:           { en: 'Explain this curve:', es: 'Explícame esta curva:', pt: 'Explica-me esta curva:', fr: FR('Explique-moi cette courbe:'), de: 'Erkläre mir diese Kurve:' },
	distance:        { en: 'Explain my distance to the market:', es: 'Explícame mi distancia al mercado:', pt: 'Explica-me a minha distância ao mercado:', fr: FR('Explique-moi mon écart au marché:'), de: 'Erkläre mir meinen Abstand zum Markt:' },
	marketMove:      { en: 'Explain this market move:', es: 'Explícame este movimiento del mercado:', pt: 'Explica-me este movimento do mercado:', fr: FR('Explique-moi ce mouvement du marché:'), de: 'Erkläre mir diese Marktbewegung:' },
	group:           { en: 'Explain this group:', es: 'Explícame este grupo:', pt: 'Explica-me este grupo:', fr: FR('Explique-moi ce groupe:'), de: 'Erkläre mir diese Gruppe:' },
	// help mode (CU-869fmufdb, UX C5): the screen's name from its help page; no gender or number agreement needed
	screen:          { en: s => `What is ${s} for, and how do I use it?`, es: s => `¿Para qué sirve ${s} y cómo se usa?`, pt: s => `Para que serve ${s} e como se usa?`, fr: s => FR(`À quoi sert ${s} et comment l’utiliser?`), de: s => `Wofür ist ${s} da und wie nutze ich es?` },
}

export const QUESTION_KINDS = Object.keys(PREFIX)

const MONTHS = {
	en: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
	es: ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'],
	pt: ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'],
	fr: ['janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin', 'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.'],
	de: ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'],
}

// German writes the day as an ordinal: "11. Nov", "10.–23. Nov"
const dayOf = (day, lang) => (langOf(lang) === 'de' ? `${day}.` : `${day}`)

// Units of the labels, per language: rental length, period, count of events; and the BI ones (week, offices, since,
// Season's comparison, a position)
const enOrdinal = n => {
	const t = n % 100
	return `${n}${t >= 11 && t <= 13 ? 'th' : ({ 1: 'st', 2: 'nd', 3: 'rd' })[n % 10] || 'th'}`
}
const UNITS = {
	en: { day: 'day', days: 'days', lastDay: 'last 1 day', lastDays: n => `last ${n} days`, event: 'event', events: 'events',
		week: n => `Week ${n}`, office: 'office', offices: 'offices', since: d => `since ${d}`,
		compare: { ly: 'vs last year', lw: 'vs last week', yday: 'vs yesterday' }, rank: (n, of) => `${enOrdinal(n)} of ${of}` },
	es: { day: 'día', days: 'días', lastDay: 'último día', lastDays: n => `últimos ${n} días`, event: 'evento', events: 'eventos',
		week: n => `semana ${n}`, office: 'oficina', offices: 'oficinas', since: d => `desde ${d}`,
		compare: { ly: 'vs año pasado', lw: 'vs semana pasada', yday: 'vs ayer' }, rank: (n, of) => `${n}.º de ${of}` },
	pt: { day: 'dia', days: 'dias', lastDay: 'último dia', lastDays: n => `últimos ${n} dias`, event: 'evento', events: 'eventos',
		week: n => `semana ${n}`, office: 'agência', offices: 'agências', since: d => `desde ${d}`,
		compare: { ly: 'vs ano passado', lw: 'vs semana passada', yday: 'vs ontem' }, rank: (n, of) => `${n}.º de ${of}` },
	fr: { day: 'jour', days: 'jours', lastDay: 'dernier jour', lastDays: n => `${n} derniers jours`, event: 'événement', events: 'événements',
		week: n => `semaine ${n}`, office: 'agence', offices: 'agences', since: d => `depuis le ${d}`,
		compare: { ly: 'vs l’an dernier', lw: 'vs semaine dernière', yday: 'vs hier' }, rank: (n, of) => `${n}${Number(n) === 1 ? 'er' : 'e'} sur ${of}` },
	de: { day: 'Tag', days: 'Tage', lastDay: 'letzter Tag', lastDays: n => `letzte ${n} Tage`, event: 'Ereignis', events: 'Ereignisse',
		week: n => `Woche ${n}`, office: 'Station', offices: 'Stationen', since: d => `seit ${d}`,
		compare: { ly: 'vs Vorjahr', lw: 'vs Vorwoche', yday: 'vs gestern' }, rank: (n, of) => `${n}. von ${of}` },
}

const langOf = lang => (lang in PREFIX.cell ? lang : 'en')

// `20261111`, `2026-11-11`, `2026-11-11T10:00:00` → { day, month } (month 0-based), or null
function partsOf(value) {
	const m = /^(\d{4})-?(\d{2})-?(\d{2})/.exec(String(value ?? '').trim())
	if (!m) return null
	const month = Number(m[2]) - 1, day = Number(m[3])
	if (month < 0 || month > 11 || day < 1 || day > 31) return null
	return { day, month }
}

export function shortDate(value, lang) {
	const p = partsOf(value)
	return p ? `${dayOf(p.day, lang)} ${MONTHS[langOf(lang)][p.month]}` : ''
}

export function dateRange(from, to, lang) {
	const a = partsOf(from), b = partsOf(to)
	if (!a || !b) return shortDate(from, lang) || shortDate(to, lang)
	if (a.day === b.day && a.month === b.month) return shortDate(from, lang)
	const months = MONTHS[langOf(lang)]
	return a.month === b.month
		? `${dayOf(a.day, lang)}–${dayOf(b.day, lang)} ${months[b.month]}`
		: `${dayOf(a.day, lang)} ${months[a.month]}–${dayOf(b.day, lang)} ${months[b.month]}`
}

const plural = (n, one, many) => `${n} ${Number(n) === 1 ? one : many}`

function labelText(label, lang) {
	if (label == null) return ''
	if (typeof label === 'number') return ''
	if (typeof label === 'string') {
		const text = label.trim()
		return /^\d+$/.test(text) ? '' : text
	}
	if (typeof label !== 'object') return ''
	const u = UNITS[langOf(lang)]
	if ('date' in label) return shortDate(label.date, lang)
	if ('range' in label) return dateRange(label.range?.[0], label.range?.[1], lang)
	if ('days' in label) return label.days == null ? '' : plural(label.days, u.day, u.days)
	if ('lastDays' in label) {
		if (label.lastDays == null) return ''
		return Number(label.lastDays) === 1 ? u.lastDay : u.lastDays(label.lastDays)
	}
	if ('events' in label) return label.events == null ? '' : plural(label.events, u.event, u.events)
	if ('value' in label) return label.value == null ? '' : String(label.value).trim()
	if ('week' in label) return label.week == null || label.week === '' ? '' : u.week(label.week)
	if ('offices' in label) return label.offices == null ? '' : plural(label.offices, u.office, u.offices)
	if ('since' in label) {
		const d = shortDate(label.since, lang)
		return d ? u.since(d) : ''
	}
	if ('compare' in label) return u.compare[label.compare] || ''
	if ('rank' in label) {
		const [n, of] = label.rank || []
		return Number.isFinite(Number(n)) && n !== null && Number.isFinite(Number(of)) && of !== null ? u.rank(Number(n), Number(of)) : ''
	}
	return ''
}

export function askQuestion(kind, labels = [], lang = 'en', { subject } = {}) {
	const byLang = PREFIX[kind]
	if (!byLang) return null
	const template = byLang[langOf(lang)]
	const prefix = typeof template === 'function' ? template(subject || '') : template
	const parts = (labels || []).map(l => labelText(l, lang)).filter(Boolean)
	return parts.length ? `${prefix} ${parts.join(' · ')}` : prefix.replace(/\s*:$/, '')
}
